import {
  ActionButton,
  Buttons,
  CancelButton,
  DeleteButton,
  DeleteConfirmation,
  Drawer,
  Field,
  Form,
  FormMessage,
  Radio,
} from '~/components';
import FieldColorSelect from './FieldColorSelect';
import FieldIconSelect from './FieldIconSelect';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';

const modeOptions = [
  { id: 'standard', name: 'Standard', description: 'Tracks time directly and has its own budget.' },
  { id: 'group', name: 'Group', description: 'Aggregates data from child tasks.' },
];

export default function TaskTypeDrawer({ taskType, onSaved, onClose, onDeleted }) {
  const title = `${taskType?.id ? 'Edit' : 'New'} Task Type`;
  useDocumentTitle(title);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting, saved }, form] = useForm();
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const [hasDependencies, setHasDependencies] = useState(true);

  const isEditing = !!taskType?.id;

  useEffect(() => {
    if (!taskType?.id) {
      setHasDependencies(false);
      return;
    }

    (async function fetchData() {
      const dependencies = await api.www.workspaces(workspace.id).projectTaskTypes(taskType.id).hasDependencies();
      setHasDependencies(dependencies.data);
    })();
  }, [api, taskType, workspace]);

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  const initialValues = mergeValues(
    {
      name: '',
      mode: 'standard',
      icon: 'file-alt',
      color: 'grey',
    },
    taskType,
  );

  return (
    <>
      <Drawer
        isOpen
        title={title}
        byline="Custom Data"
        onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
        onClose={handleClose}>
        {(closeDrawer) => {
          const handleCloseClick = () => dirtyCheck(() => closeDrawer());

          async function handleSubmit(values, { resetForm }) {
            try {
              form.submit(formRef.current.status.action);

              const body = emptyStringToNull(values);

              const { data } = await api.www
                .workspaces(workspace.id)
                .projectTaskTypes(taskType ? taskType.id : undefined)
                .upsert(body);

              await onSaved(data);
              form.save(formRef.current.status.action);

              switch (formRef.current.status.action) {
                case 'new':
                  resetForm();
                  toast.success('Task Type successfully saved.');
                  break;
                case 'close':
                  closeDrawer();
                  break;
              }
              form.done();
            } catch ({ message }) {
              form.error({ message });
            }
          }

          async function handleDelete() {
            const confirm = await confirmation.prompt((resolve) => (
              <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this Task Type?</DeleteConfirmation>
            ));
            if (!confirm) return;
            try {
              await api.www.workspaces(workspace.id).projectTaskTypes(taskType.id).delete();
              await onDeleted();
              closeDrawer();
            } catch ({ message }) {
              toast.error(message);
            }
          }

          return (
            <Formik
              innerRef={formRef}
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnBlur={false}
              validateOnChange={false}
              validationSchema={Yup.object().shape({
                name: Yup.string().label('Name').max(255).required(),
                mode: Yup.string().label('Mode').oneOf(['standard', 'group']).required(),
                icon: Yup.string().label('Icon').max(255).required(),
                color: Yup.string().label('Color').max(255).required(),
              })}>
              {(formik) => {
                const submit = async (action) => {
                  formik.setStatus({ action });
                  formik.submitForm();
                };

                // Mode cannot be changed if the type has dependencies
                const isModeDisabled = isEditing && hasDependencies;

                return (
                  <Form>
                    <Form.Section title="General">
                      <Form.Control>
                        <Field.Text autoFocus name="name" placeholder="Name" maxLength={255} />
                      </Form.Control>
                      <Form.Control>
                        <FieldColorSelect name="color" placeholder="Color" />
                        <FieldIconSelect name="icon" placeholder="Icon" color={formik.values.color} />
                      </Form.Control>
                    </Form.Section>

                    <Form.Section
                      title="Mode"
                      subtitle="Standard types track time directly. Group types aggregate data from child tasks.">
                      <Form.Control>
                        <Field.RadioGroup name="mode" direction="vertical">
                          {modeOptions.map((option) => (
                            <Radio key={option.id} value={option.id} label={option.name} disabled={isModeDisabled} />
                          ))}
                        </Field.RadioGroup>
                      </Form.Control>
                    </Form.Section>

                    {status && <FormMessage.Error>{message}</FormMessage.Error>}

                    <Drawer.Actions>
                      {taskType.id && !hasDependencies && <DeleteButton onClick={handleDelete}>Delete</DeleteButton>}

                      <Buttons align="right">
                        <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                        {!taskType?.id && (
                          <ActionButton
                            isLoading={isSubmitting === 'new'}
                            ok={saved === 'new'}
                            type="submit"
                            onClick={() => submit('new')}>
                            Save &amp; New
                          </ActionButton>
                        )}

                        <ActionButton
                          isLoading={isSubmitting === 'close'}
                          ok={saved === 'close'}
                          type="submit"
                          onClick={() => submit('close')}>
                          Save &amp; Close
                        </ActionButton>
                      </Buttons>
                    </Drawer.Actions>
                  </Form>
                );
              }}
            </Formik>
          );
        }}
      </Drawer>
    </>
  );
}
